import { useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import {
	useCreateTeam,
	useDeleteTeam,
	useTeamsList,
} from "@/hooks/use-admin-l4";
import { extractErrorMessage } from "@/hooks/use-auth-api";

export function TeamsTab() {
	const { data: teams, refetch } = useTeamsList();
	const createMutation = useCreateTeam();
	const deleteMutation = useDeleteTeam();

	const [newName, setNewName] = useState("");
	const [newDesc, setNewDesc] = useState("");
	const [error, setError] = useState<string | null>(null);

	const submit = async () => {
		if (!newName.trim()) return;
		setError(null);
		try {
			await createMutation.mutateAsync({
				name: newName.trim(),
				description: newDesc.trim() || undefined,
			});
			setNewName("");
			setNewDesc("");
			refetch();
		} catch (err) {
			setError(extractErrorMessage(err, "Création impossible"));
		}
	};

	return (
		<div className="stack">
			<Card>
				<CardHead
					title="Créer une équipe"
					sub="Regroupez les utilisateurs pour faciliter l'affectation"
				/>
				<CardBody>
					<div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
						<div style={{ flex: 1 }}>
							<FormRow label="Nom" required>
								<input
									className="aform-i"
									data-testid="team-name"
									placeholder="Catalogue Mode, Support FR, Logistique Yaoundé..."
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
								/>
							</FormRow>
						</div>
						<div style={{ flex: 2 }}>
							<FormRow label="Description">
								<input
									className="aform-i"
									data-testid="team-desc"
									placeholder="Optionnel"
									value={newDesc}
									onChange={(e) => setNewDesc(e.target.value)}
								/>
							</FormRow>
						</div>
						<AdminButton
							variant="primary"
							onClick={submit}
							disabled={!newName.trim() || createMutation.isPending}
							data-testid="team-create-submit"
							style={{ marginBottom: 14 }}
						>
							<AdminIcon n="plus" s={14} />
							Créer
						</AdminButton>
					</div>
					{error && (
						<div className="auth-alert danger" data-testid="team-error">
							<span className="icon">
								<AdminIcon n="alert" s={16} />
							</span>
							<div>{error}</div>
						</div>
					)}
				</CardBody>
			</Card>

			<Card>
				<CardHead title={`Équipes · ${teams?.length ?? 0}`} />
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Nom</th>
								<th>Description</th>
								<th>Manager</th>
								<th className="right">Membres</th>
								<th>Créée</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{!teams && (
								<tr>
									<td colSpan={6} className="muted center">
										Chargement…
									</td>
								</tr>
							)}
							{teams && teams.length === 0 && (
								<tr>
									<td colSpan={6} className="muted center">
										Aucune équipe créée.
									</td>
								</tr>
							)}
							{teams?.map((t) => (
								<tr key={t.id} data-testid={`team-row-${t.name}`}>
									<td>
										<strong>{t.name}</strong>
									</td>
									<td className="muted">{t.description ?? "—"}</td>
									<td>
										{t.manager ? (
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
												}}
											>
												<AdminAvatar name={t.manager.fullName} />
												<span style={{ fontSize: 12 }}>
													{t.manager.fullName}
												</span>
											</div>
										) : (
											<span className="muted">—</span>
										)}
									</td>
									<td className="right tn">{t.memberCount}</td>
									<td className="muted" style={{ fontSize: 11 }}>
										{new Date(t.createdAt).toLocaleDateString("fr-FR")}
									</td>
									<td>
										<button
											className="top-btn"
											onClick={() => deleteMutation.mutate(t.id)}
											data-testid={`team-delete-${t.name}`}
											title="Supprimer"
										>
											<AdminIcon n="close" s={14} c="var(--a-danger)" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>
		</div>
	);
}
